// The report TEMPLATE SYSTEM (F4.5) — readiness constraint C4 made code:
// reports compose from the FROZEN evaluation snapshot through a
// structure-driven template. This module is the system's core: a versioned
// template identity, and a PURE model builder that derives every section
// from the snapshot's own structure (blocks, dimensions, findings,
// recommendations) plus the managed register rows. Nothing here fetches,
// computes a score, or knows a single instrument-specific key — the Project
// Artifact Quality report is this template applied to that instrument's
// snapshot, not a PAQ report hard-coded (C1/C2/C4).
//
// AUDIENCE, stated structurally: Release 1 renders the INTERNAL WORKING
// REPORT only. The disclosure policy (BACKLOG §12) has not been decided, so
// no client-safe variant exists yet — `audience` admits one value the same
// way findings.client_visibility admits only 'internal'. Widening it is a
// reviewed change once that policy lands, and the label the template prints
// comes from here, not from screen copy someone might soften.
//
// The template identity travels ON the rendered report beside the replay
// identity (hashes, evaluation id, seq): a report is reproducible only if
// both the data identity AND the presentation identity are recorded.

import type {
  ActionFindingLink,
  ActionRow,
  EvaluationRow,
  EvaluationSnapshot,
  FindingKernelOrigin,
  FindingRow,
  MemberDirectoryEntry,
} from "./types";

export const WORKING_REPORT_TEMPLATE = {
  id: "assessment-working-report",
  // 1.1.0 adds `scoreInterpretation` (F5.3a gate G14). A printed statement is
  // template content, so it moves the template identity the report carries.
  version: "1.1.0",
  audience: "internal" as const,
  label:
    "INTERNAL WORKING REPORT — not for client distribution; the disclosure policy (BACKLOG §12) has not been decided.",
  // Gate G14 of the harmonized-scale ruling. It lives HERE, beside the
  // audience label and for the same reason: it is a statement the report must
  // make, not screen copy someone could soften. It is true under every
  // instrument version — the scale's anchors travel inside the definition
  // hash, so the number and the definition are one claim, never two.
  scoreInterpretation:
    "A maturity level means what the definition recorded above says it means: the scale and its anchors travel inside the definition hash. Levels produced under different definition versions are not comparable as numbers however alike they look, and no score is converted between versions.",
};

export type Currency =
  | { kind: "current" }
  | { kind: "stale" }
  | { kind: "unknown"; reason: string };

export interface ReportIdentity {
  clientName: string;
  engagementName: string;
  entityName: string;
  runTitle: string;
  runState: string;
  instrumentTitle: string;
  instrumentVersion: string;
}

export interface RegisterFindingModel {
  finding: FindingRow;
  origin: FindingKernelOrigin | null;
}

export interface RegisterActionModel {
  action: ActionRow;
  ownerEmail: string;
  /** Titles of the findings this action CURRENTLY addresses — active link
   *  rows only; history stays in the register, not the report. */
  addresses: string[];
}

export interface ReportModel {
  template: typeof WORKING_REPORT_TEMPLATE;
  identity: ReportIdentity;
  currency: Currency;
  generatedAt: string;
  evaluation: {
    id: string;
    seq: number;
    evaluatedAt: string;
    definitionHash: string;
    policyHash: string;
    inputHash: string;
    resultHash: string;
    policyId: string;
    engineContract: string;
    canonicalization: string;
  };
  /** The frozen kernel result, VERBATIM — sections iterate its structure. */
  result: EvaluationSnapshot["result"];
  register: {
    findings: RegisterFindingModel[];
    actions: RegisterActionModel[];
  };
}

/**
 * Pure assembly: everything display-worthy, nothing computed. The one
 * derivation is JOINING register rows (finding↔origin, action↔active links
 * ↔finding titles, owner↔email) — identity lookups, never arithmetic.
 */
export function buildReportModel(input: {
  identity: ReportIdentity;
  evaluation: EvaluationRow;
  currency: Currency;
  findings: FindingRow[];
  origins: FindingKernelOrigin[];
  actions: ActionRow[];
  links: ActionFindingLink[];
  members: MemberDirectoryEntry[];
  now: Date;
}): ReportModel {
  const originByFinding = new Map(input.origins.map((o) => [o.finding_id, o]));
  const findingById = new Map(input.findings.map((f) => [f.id, f]));
  const emailByUser = new Map(input.members.map((m) => [m.user_id, m.email]));
  const runFindingIds = new Set(input.findings.map((f) => f.id));

  const actions = input.actions
    .filter((a) =>
      input.links.some((l) => l.action_id === a.id && runFindingIds.has(l.finding_id)),
    )
    .map((a) => ({
      action: a,
      ownerEmail: emailByUser.get(a.owner_id) ?? a.owner_id,
      addresses: input.links
        .filter((l) => l.action_id === a.id && l.removed_at === null)
        .map((l) => findingById.get(l.finding_id)?.title ?? l.finding_id),
    }));

  return {
    template: WORKING_REPORT_TEMPLATE,
    identity: input.identity,
    currency: input.currency,
    generatedAt: input.now.toISOString(),
    evaluation: {
      id: input.evaluation.id,
      seq: input.evaluation.seq,
      evaluatedAt: input.evaluation.evaluated_at,
      definitionHash: input.evaluation.definition_hash,
      policyHash: input.evaluation.policy_hash,
      inputHash: input.evaluation.input_hash,
      resultHash: input.evaluation.result_hash,
      policyId: input.evaluation.policy_id,
      engineContract: input.evaluation.engine_contract_version,
      canonicalization: input.evaluation.canonicalization,
    },
    result: input.evaluation.result.result,
    register: {
      findings: input.findings.map((f) => ({
        finding: f,
        origin: originByFinding.get(f.id) ?? null,
      })),
      actions,
    },
  };
}
